import axios, {
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios';
import authService from './auth-service';
import { ErrorHandler } from '@/lib/error-handler';

class ApiService {
  private client: AxiosInstance;
  private isRefreshing = false;
  private failedQueue: Array<{
    resolve: (token: string) => void;
    reject: (error: unknown) => void;
  }> = [];

  constructor() {
    this.client = axios.create({
      baseURL: import.meta.env.VITE_API_URL || 'http://localhost:3000',
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors() {
    // Request interceptor for auth token
    this.client.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        const token = authService.getAccessToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor for error handling and token refresh
    this.client.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        // Handle 401 errors
        if (error.response?.status === 401 && !originalRequest._retry) {
          // Special handling for logout endpoint - don't create a loop
          if (originalRequest.url?.includes('/auth/logout')) {
            // Skip the API call and just clear tokens locally
            await authService.logout(true);
            window.location.href = '/login';
            // Transform error using ErrorHandler before rejecting
            const appError = ErrorHandler.transformError(error);
            return Promise.reject(appError);
          }

          // Don't try to refresh for other auth endpoints
          if (originalRequest.url?.includes('/auth/')) {
            // Clear tokens and redirect to login for auth endpoint failures
            // Skip API call to prevent loop
            await authService.logout(true);
            window.location.href = '/login';
            // Transform error using ErrorHandler before rejecting
            const appError = ErrorHandler.transformError(error);
            return Promise.reject(appError);
          }

          if (this.isRefreshing) {
            // If we're already refreshing, queue this request
            return new Promise((resolve, reject) => {
              this.failedQueue.push({ resolve, reject });
            }).then((token) => {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              return this.client(originalRequest);
            });
          }

          // Since refresh token endpoint is not implemented in backend,
          // immediately redirect to login on 401
          originalRequest._retry = true;

          // Clear tokens and redirect to login
          // Skip API call to prevent potential loops
          await authService.logout(true);
          window.location.href = '/login';
          // Transform error using ErrorHandler before rejecting
          const appError = ErrorHandler.transformError(error);
          return Promise.reject(appError);

          // TODO: Uncomment this block when /auth/refresh endpoint is implemented
          // this.isRefreshing = true;
          // try {
          //   const hasRefreshToken = await authService.getRefreshToken();
          //   if (!hasRefreshToken) {
          //     throw new Error('No refresh token available');
          //   }
          //   const newToken = await authService.refreshToken();
          //   this.processQueue(null, newToken);
          //   originalRequest.headers.Authorization = `Bearer ${newToken}`;
          //   return this.client(originalRequest);
          // } catch (refreshError) {
          //   this.processQueue(refreshError, null);
          //   await authService.logout();
          //   window.location.href = '/login';
          //   return Promise.reject(refreshError);
          // } finally {
          //   this.isRefreshing = false;
          // }
        }

        // Transform error using ErrorHandler before rejecting
        const appError = ErrorHandler.transformError(error);
        return Promise.reject(appError);
      }
    );
  }

  // Generic request methods - simplified without offline support
  async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse<T> = await this.client.get(url, config);
    return response.data;
  }

  async post<T>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ): Promise<T> {
    if (!navigator.onLine) {
      const offlineError = ErrorHandler.transformError(
        new Error(
          'No internet connection. Please check your network and try again.'
        )
      );
      throw offlineError;
    }

    const response: AxiosResponse<T> = await this.client.post(
      url,
      data,
      config
    );
    return response.data;
  }

  async put<T>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ): Promise<T> {
    if (!navigator.onLine) {
      const offlineError = ErrorHandler.transformError(
        new Error(
          'No internet connection. Please check your network and try again.'
        )
      );
      throw offlineError;
    }

    const response: AxiosResponse<T> = await this.client.put(url, data, config);
    return response.data;
  }

  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    if (!navigator.onLine) {
      const offlineError = ErrorHandler.transformError(
        new Error(
          'No internet connection. Please check your network and try again.'
        )
      );
      throw offlineError;
    }

    const response: AxiosResponse<T> = await this.client.delete(url, config);
    return response.data;
  }

  async patch<T>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ): Promise<T> {
    if (!navigator.onLine) {
      const offlineError = ErrorHandler.transformError(
        new Error(
          'No internet connection. Please check your network and try again.'
        )
      );
      throw offlineError;
    }

    const response: AxiosResponse<T> = await this.client.patch(
      url,
      data,
      config
    );
    return response.data;
  }
}

export default new ApiService();
